import { Button, Grid, Popover, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import React, { MouseEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Datatable, { PagingTypeEnum } from '../Datatable/Datatable';
import DatatableColumnModel from '../Datatable/DatatableColumnModel';
import { OrderDirection } from '../Datatable/OrderDirection';
import { setComponentType, setExcludedCompanies, setMatchingOrderBy, setMatchingOrderDirection, setMatchingQuery, setOpenModal, setTriggerMarketingMatching, setTriggerMatching } from '../MatchingPreview/reducers/MatchingPreviewReducer';
import TonnageModel from './models/TonnageModel';
import { setSelectedTonnageId, tonnageInputSelector } from './reducers/TonnageInputReducer';
import { SearchType, setCheckedTonnages, setDatatableSelectedTonnageIndex, setOrderBy, setOrderDirection, setSearchResultTotalRecord, setSearchType, setTonnageSearchResults, setTriggerTonnageSearch, tonnageSearchSelector } from './reducers/TonnageSearchReducer';
import { matchingPreviewSelector } from '../MatchingPreview/reducers/MatchingPreviewReducer';
import { ComponentType } from '../SavedSearch/SavedSearch';
import MatchingApi, { MatchingQueryParam } from '../MatchingPreview/api/MatchingApi';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import Matching, { MatchingType } from '../MatchingPreview/MatchingConstants';
import { QueryItem } from './models/TonnageQueryModel';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import { TonnageMatchingDisplayModel } from './models/TonnageMatchingDisplayModel';
import { CargoMatchingDisplayModel } from '../Cargo/models/CargoMatchingDisplayModel';
import { userProfileSelector } from '../Login/reducers/UserProfileReducer';
import TonnageApi, { DeleteTonnageParam } from './api/TonnageApi';
import Swal from 'sweetalert2';
import { UserGroupEnum } from '../UserManagement/UserGroupEnum';

const TonnageTable: React.FunctionComponent = () => {
    let dispatch = useDispatch();
    let history = useHistory();

    let tonnageData = useSelector(tonnageSearchSelector);
    const tonnageInputViewData = useSelector(tonnageInputSelector);
    const matchingSelectorData = useSelector(matchingPreviewSelector);
    const userProfile = useSelector(userProfileSelector);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [overviewFullTc, setOverviewFullTc] = useState<string>("");

    const divRef = React.createRef<HTMLDivElement>();

    if (matchingSelectorData.triggerMatching === 0) {
        const urlParams = new URLSearchParams(window.location.search);

        let componentType = urlParams.get("componentType");
        if (componentType) {
            let matchingQueryString = urlParams.get("matchingQuery");
            if (matchingQueryString) {
                let matchingQuery = JSON.parse(decodeURIComponent(matchingQueryString)) as MatchingQueryParam;
                dispatch(setMatchingQuery(matchingQuery));
                dispatch(setComponentType(ComponentType.Tonnage));
                dispatch(setTriggerMatching((new Date()).getTime()));
            }
        }

        let matchingType = urlParams.get("matchingType");
        let matchingModelString = urlParams.get("matchingModel");
        if (matchingModelString) {
            let matchingModel = JSON.parse(decodeURIComponent(matchingModelString)) as CargoMatchingDisplayModel;
            let detailString = matchingType + " for {" + matchingModel.chartererCompanyName + "} {"
                + matchingModel.cargoTypeName + "} {"
                + matchingModel.dwtMin.toLocaleString() + " - " + matchingModel.dwtMax.toLocaleString() + "} {"
                + matchingModel.loadPortName + " - " + matchingModel.disportName + "}";

            Utility.Alert.Info(detailString, 10000000000);
        }
    }

    useEffect(() => {
        if (matchingSelectorData.type === ComponentType.Tonnage) {
            dispatch(showLoading(true));

            MatchingApi.MatchingTonnage(matchingSelectorData.matchingQuery)
                .then((response: BaseResponse<TonnageModel[]>) => {
                    if (response.success) {
                        dispatch(setTonnageSearchResults(response.data));
                        dispatch(setSearchResultTotalRecord(response.totalRecord));
                        dispatch(setSearchType(SearchType.Matching));
                    } else {
                        Utility.Alert.Error(response.message);
                    }
                }).catch((error) => {
                    Utility.Alert.Error(error);
                })
                .finally(() => {
                    dispatch(showLoading(false));
                });
        }
    }, [matchingSelectorData.triggerMatching])

    const columns = [
        {
            Field: "vesselName",
            Title: "Vessel Name",
            AllowSort: true,
            Render: (rowData: TonnageModel) => {
                let previewButton = rowData.fullTcpreview && rowData.fullTcpreview.length > 0 ?
                    <i className="fa fa-eye" aria-hidden="true"></i> :
                    <i className="fa fa-eye-slash" aria-hidden="true"></i>;
                return <>
                    <Grid container>
                        <Grid item xs={11}>
                            {rowData.vesselName}
                        </Grid>
                        <Grid item xs={1}>
                            <Button
                                className="tonnage-table-preview-full-tc"
                                onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
                                    if (rowData.fullTcpreview && rowData.fullTcpreview.length > 0) {
                                        setAnchorEl(event.currentTarget);
                                        setOverviewFullTc(rowData.fullTcpreview);
                                    }
                                }}
                                onMouseLeave={() => {
                                    setAnchorEl(null)
                                }}
                                disabled={!rowData.fullTcpreview || rowData.fullTcpreview.length === 0}
                            >
                                {previewButton}
                            </Button>
                        </Grid>
                    </Grid>
                </>
            }
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "dwt",
            Title: "DWT",
            AllowSort: true,
            Render: (rowData: TonnageModel) => {
                return <>{rowData.dwt.toLocaleString()}</>;
            }
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "yearBuilt",
            Title: "BLT",
            AllowSort: true,
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "portName",
            Title: "Port",
            AllowSort: true,
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "portArea",
            Title: "Area",
            AllowSort: true,
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "dateOpenStart",
            Title: "Date",
            AllowSort: true,
            Render: (rowData: TonnageModel) => (
                <>
                    {formatDateMMMDD(rowData.dateOpenStart).toUpperCase() + " - " + formatDateMMMDD(rowData.dateOpenEnd).toUpperCase()}
                </>
            )
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "operatorCountry",
            Title: "Country",
            AllowSort: true,
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "updatedDate",
            Title: "Update Time",
            AllowSort: true,
            Render: (rowData: TonnageModel) => (
                <>
                    {formatUpdatedDate(rowData.updatedDate)}
                </>
            )
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "userDisplayName",
            Title: "By",
            AllowSort: true,
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "operatorEmails",
            Title: "Emails",
            AllowSort: false,
            Width: "50px",
            Render: (rowData: TonnageModel) => {
                return <>
                    <Button
                        className="table-emails"
                        onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
                            if (rowData.operatorEmails && rowData.operatorEmails.length > 0) {
                                setAnchorEl(event.currentTarget);
                                setOverviewFullTc(rowData.operatorEmails);
                            }
                        }}
                        onMouseLeave={() => {
                            setAnchorEl(null)
                        }}
                        disabled={!rowData.operatorEmails || rowData.operatorEmails.length === 0}
                    >
                        <i className="fa fa-envelope-o" aria-hidden="true"></i>
                    </Button>
                </>
            }
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "",
            Title: "",
            AllowSort: false,
            Render: (rowData: TonnageModel) => (
                <>
                    <Tooltip title="Matching Areas 1">
                        <Button
                            className='table-action-button'
                            onClick={() => {
                                matchingAreas(rowData, MatchingType.MatchingArea1);
                            }}
                            variant='outlined'
                        >M1</Button>
                    </Tooltip>
                    <Tooltip title="Matching Areas 2">
                        <Button
                            className='table-action-button'
                            onClick={() => {
                                matchingAreas(rowData, MatchingType.MatchingArea2);
                            }}
                            variant='outlined'
                        >M2</Button>
                    </Tooltip>
                    <Tooltip title="Marketing Matching">
                        <Button
                            className='table-action-button'
                            onClick={() => {
                                matchingAreas(rowData, MatchingType.MarketingMatching);
                            }}
                            variant='outlined'
                        >M</Button>
                    </Tooltip>
                    {userProfile.userGroup === UserGroupEnum.Administrator &&
                        <Tooltip title="Delete Tonnage">
                            <Button
                                className='table-action-button'
                                variant='outlined'
                                color='error'
                                onClick={() => {
                                    deleteTonnage(rowData.id)
                                }}
                            >
                                <i className="fa fa-trash" aria-hidden="true"></i>
                            </Button>
                        </Tooltip>
                    }
                </>
            )
        } as DatatableColumnModel<TonnageModel>,
    ];

    const columnsForVesselCopy = [
        {
            Field: "vesselName",
            Title: "Vessel Name",
            AllowSort: true,
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "dwt",
            Title: "DWT",
            AllowSort: true,
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "yearBuilt",
            Title: "BLT",
            AllowSort: true,
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "portName",
            Title: "Port",
            AllowSort: true,
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "dateOpenStart",
            Title: "Date",
            AllowSort: true,
            Render: (rowData: TonnageModel) => (
                <>
                    {formatDateMMMDD(rowData.dateOpenStart).toUpperCase() + " - " + formatDateMMMDD(rowData.dateOpenEnd).toUpperCase()}
                </>
            )
        } as DatatableColumnModel<TonnageModel>,
    ];

    const deleteTonnage = (id: string) => {
        Swal.fire({
            title: 'Are you sure want to delete this tonnage? This action can not be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));
                TonnageApi.Delete({
                    id: id
                } as DeleteTonnageParam)
                    .then((response: BaseResponse<TonnageModel[]>) => {
                        if (response.success) {
                            Utility.Alert.Success("Tonnage deleted successful");

                            dispatch(showLoading(false));

                            dispatch(setTriggerTonnageSearch(new Date().getTime()));
                        }
                    });
            }
        })
    };

    const formatDateMMMDD = (dateString: string): string => {
        let date = new Date(dateString);
        return moment(date).format("DD MMM");
    };

    const formatUpdatedDate = (dateString: string): string => {
        let date = new Date(dateString);
        return moment(date).format("DD/MM/YYYY HH:mm");
    };

    const getTableClassName = () => {
        let className = "tonnage-datatable-search-result nice-scroll";

        if (!tonnageInputViewData.tonnageQuickInputExpand && tonnageInputViewData.tonnageAdvanceInputExpand) {
            className += " tonnage-datatable-search-result-quick-input-collapse-only";
        } else if (tonnageInputViewData.tonnageQuickInputExpand && !tonnageInputViewData.tonnageAdvanceInputExpand) {
            className += " tonnage-datatable-search-result-advanced-input-collapse-only";
        } else if (!tonnageInputViewData.tonnageQuickInputExpand && !tonnageInputViewData.tonnageAdvanceInputExpand) {
            className += " tonnage-datatable-search-result-all-input-collapse";
        }

        return className;
    }

    useEffect(() => {
        if (tonnageData.triggerMatchingAreaModel.triggerMatchingArea > 0) {
            matchingAreas(tonnageData.triggerMatchingAreaModel.model, tonnageData.triggerMatchingAreaModel.matchingType);
        }
    }, [tonnageData.triggerMatchingAreaModel.triggerMatchingArea])

    const matchingAreas = (rowData: TonnageModel, matchingType: MatchingType) => {
        let lay = "", can = "";
        let matchingArea = Matching.getMatchingAreas(rowData.portArea, rowData.dwt);
        let areaQuery = [] as QueryItem[];
        let excludedCountries = [] as QueryItem[];
        let excludedCompanies = [] as QueryItem[];
        let marketingMatching = false;

        if (!matchingArea) {
            Utility.Alert.Error("Can not matching with area: " + rowData.portArea);
            return;
        }

        if (matchingType === MatchingType.MatchingArea1) {
            lay = Utility.DateFormat.format(Utility.addDaysToDate(new Date(rowData.dateOpenStart), 5));
            can = Utility.DateFormat.format(Utility.addDaysToDate(new Date(rowData.dateOpenEnd), -3));

            areaQuery = _.map(matchingArea.first, (matchingArea) => {
                return {
                    id: matchingArea,
                    value: matchingArea,
                    positive: true
                } as QueryItem;
            })
            marketingMatching = false;
        } else if (matchingType === MatchingType.MatchingArea2) {
            lay = Utility.DateFormat.format(Utility.addDaysToDate(new Date(rowData.dateOpenStart), 5));
            can = Utility.DateFormat.format(Utility.addDaysToDate(new Date(rowData.dateOpenEnd), -5));

            areaQuery = _.map(matchingArea.second, (matchingArea) => {
                return {
                    id: matchingArea,
                    value: matchingArea,
                    positive: true
                } as QueryItem;
            })
            marketingMatching = false;
        } else {
            let marketingAreas = [...matchingArea.first, ...matchingArea.second];
            areaQuery = _.map(marketingAreas, (matchingArea) => {
                return {
                    id: matchingArea,
                    value: matchingArea,
                    positive: true
                } as QueryItem;
            })

            if (rowData.operatorCountry) {
                excludedCountries = [{
                    id: rowData.operatorCountry,
                    value: rowData.operatorCountry,
                    positive: true
                } as QueryItem];
            }

            if (rowData.operatorName) {
                excludedCompanies = [{
                    id: rowData.operatorName,
                    value: rowData.operatorName,
                    positive: true
                } as QueryItem]
            }

            marketingMatching = true;
        }

        let matchingQuery = {
            lay: lay,
            can: can,
            dwtMin: 0,
            dwtMax: 0,
            tonnageDwt: rowData.dwt,
            loadAreas: areaQuery,
            disAreas: [],
            excludedCountries: excludedCountries,
            excludedCompanies: excludedCompanies,
            marketingMatching: marketingMatching,
            orderDirection: OrderDirection.Desc,
            orderBy: "updatedDate"
        } as MatchingQueryParam;

        dispatch(setMatchingQuery(matchingQuery));

        if (marketingMatching) {
            //marketing matching
            dispatch(setComponentType(ComponentType.Tonnage));
            dispatch(setOpenModal(true));
            dispatch(setTriggerMarketingMatching((new Date()).getTime()));
        } else {
            //matching
            let selectedMatchingTonnage = {
                vesselName: rowData.vesselName,
                dwt: rowData.dwt,
                portName: rowData.portName,
                dateOpenStart: rowData.dateOpenStart,
                dateOpenEnd: rowData.dateOpenEnd,
            } as TonnageMatchingDisplayModel;

            let json = JSON.stringify(matchingQuery);

            let matchingTypeString = matchingType == MatchingType.MatchingArea1 ? "M1" : "M2";

            let url = "/cargo?componentType=" + ComponentType.Cargo + "&matchingQuery=" + encodeURIComponent(json) + "&matchingType=" + matchingTypeString + "&matchingModel=" + encodeURIComponent(JSON.stringify(selectedMatchingTonnage));

            const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
            if (newWindow) newWindow.opener = null
        }
    }

    return <div className='datatable-wrapper'>
        <Grid
            container
            justifyContent="flex-end"
            className="table-search-result-utility-bar"
        >
            <Grid>
                <Button
                    color="secondary"
                    variant='outlined'
                    className='table-search-result-utility-bar-item'
                    onClick={() => {
                        dispatch(setOpenModal(true));
                    }}
                >Preview Matching</Button>
                <Button
                    color="secondary"
                    variant='outlined'
                    className='table-search-result-utility-bar-item'
                    onClick={() => {
                        let emails = "";
                        _.each(tonnageData.checkedTonnages, (tonnage) => {
                            if (tonnage.operatorEmails && tonnage.operatorEmails.length > 0) {
                                emails += tonnage.operatorEmails + ";";
                            }
                        });
                        emails = emails.replace(/\;\;/g,';')
                        navigator.clipboard.writeText(emails);
                        Utility.Alert.Success("Copied to clipboard");
                    }}
                >Copy emails to clipboard</Button>
                <Button
                    color="secondary"
                    variant='outlined'
                    className='table-search-result-utility-bar-item'
                    onClick={() => {
                        let div = document.getElementById("tonnage-table-copy");
                        if (!div) {
                            return;
                        }

                        div.className = "tonnage-table-copy-show";

                        let selection = window.getSelection();
                        if (selection) {
                            let range = document.createRange();
                            range.selectNodeContents(divRef.current as Node);
                            selection.removeAllRanges();
                            selection.addRange(range);
                            document.execCommand("copy");
                            Utility.Alert.Success("Copied to clipboard");
                        }

                        div.className = "tonnage-table-copy-hide";
                    }}
                >Copy vessel list to clipboard</Button>
            </Grid>
            <Grid
                className='table-search-result-utility-bar-item table-search-result-utility-bar-result-found'
            >
                {tonnageData.searchResultTotalRecord + " results found"}
            </Grid>
        </Grid>
        <Datatable<TonnageModel>
            useCheckBox={true}
            useDefaultSearchBar={false}
            pagingType={PagingTypeEnum.None}
            data={tonnageData.tonnageSearchResults}
            useItemPerPage={false}
            columns={columns}
            isDataLoaded={true}
            className={getTableClassName()}
            useHeaderSort={true}
            onOrderDirectionChange={(column: DatatableColumnModel<TonnageModel>, orderDirection: OrderDirection) => {
                if (tonnageData.searchType === SearchType.Search) {
                    dispatch(setOrderBy(column.Field));
                    dispatch(setOrderDirection(orderDirection));
                    dispatch(setTriggerTonnageSearch((new Date()).getTime()));
                } else {
                    dispatch(setMatchingOrderBy(column.Field));
                    dispatch(setMatchingOrderDirection(orderDirection));
                    dispatch(setTriggerMatching((new Date()).getTime()));
                }
            }}
            onRowSelectedChange={(tonnage: TonnageModel, index: number, event: MouseEvent<HTMLElement>) => {
                var target = event.target as HTMLElement;
                if (target.tagName !== "BUTTON") {
                    dispatch(setSelectedTonnageId(tonnage.id))
                    dispatch(setDatatableSelectedTonnageIndex(index));
                }
            }}
            selectedRowIndex={tonnageData.datatableSelectedTonnageIndex}
            stickyHeader={true}
            orderColumn={tonnageData.searchType === SearchType.Search ? tonnageData.tonnageQuery.orderBy : matchingSelectorData.matchingQuery.orderBy}
            orderDirection={tonnageData.searchType === SearchType.Search ? tonnageData.tonnageQuery.orderDirection : matchingSelectorData.matchingQuery.orderDirection}
            onTableCheckboxSelectedChange={(selectedRows: TonnageModel[]) => {
                dispatch(setCheckedTonnages(selectedRows));
            }}
        ></Datatable>
        <Popover
            id="mouse-over-popover"
            sx={{
                pointerEvents: 'none',
            }}
            open={anchorEl != null}
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            disableRestoreFocus
        >
            <Typography sx={{ p: 1 }}>{overviewFullTc}</Typography>
        </Popover>
        <div id="tonnage-table-copy"
            className='tonnage-table-copy-hide'
            contentEditable="true"
            ref={divRef}
        >
            <Datatable<TonnageModel>
                useDefaultSearchBar={false}
                pagingType={PagingTypeEnum.None}
                data={tonnageData.checkedTonnages}
                useItemPerPage={false}
                columns={columnsForVesselCopy}
                isDataLoaded={true}
                className={getTableClassName()}
            ></Datatable>
        </div>
    </div>
};

export default TonnageTable;